import React, { memo } from "react";
import { Link } from "react-router-dom";
import Avatar from "../../assets/images/icon/placeholder_Awatar.png";
import { _imagesURL } from "../../constants/API";
import "./ProfileAvatar.css";

/**
 * ProfileAvatar — Standardized Profile Avatar Component
 * Ensures exact 1:1 circular ratio, anti-distortion object-cover,
 * and reliable immediate image rendering with zero stuck shimmer loaders.
 *
 * @param {string} src - Raw image path or full URL.
 * @param {number|string} size - Avatar dimension in px (e.g. 38, 44, 50). Default is 40.
 * @param {'gradient'|'subtle'|'cyan'|'plain'} variant - Ring style. Default is 'gradient'.
 * @param {boolean} isOnline - Show online indicator dot. Default is false.
 * @param {string} link - Optional route URL to wrap in a Link.
 * @param {string} alt - Alt text. Default is "Profile".
 * @param {string} className - Additional CSS class for outer container.
 * @param {object} style - Additional inline styles for outer container.
 * @param {function} onClick - Click handler.
 */
function ProfileAvatar({
  src,
  size = 40,
  variant = "gradient",
  isOnline = false,
  link,
  alt = "Profile",
  className = "",
  style = {},
  onClick,
}) {
  // Resolve image source with CDN fallback
  const resolvedSrc = React.useMemo(() => {
    if (!src || src === Avatar) return Avatar;
    if (typeof src !== "string") return Avatar;
    const trimmed = src.trim();
    if (!trimmed) return Avatar;
    if (
      trimmed.startsWith("http://") ||
      trimmed.startsWith("https://") ||
      trimmed.startsWith("data:") ||
      trimmed.startsWith("blob:") ||
      trimmed.includes("placeholder_Awatar") ||
      trimmed.includes("assets/") ||
      trimmed.includes("@fs") ||
      trimmed.includes("node_modules")
    ) {
      return trimmed;
    }
    // Clean base and path to guarantee exactly one slash between domain and path
    const base = _imagesURL.trim().replace(/\/+$/, "");
    const path = trimmed.replace(/^\/+/, "");
    return `${base}/${path}`;
  }, [src]);

  const numericSize = typeof size === "number" ? size : parseInt(size, 10) || 40;
  const sizeStyle = {
    width: `${numericSize}px`,
    height: `${numericSize}px`,
    minWidth: `${numericSize}px`,
    minHeight: `${numericSize}px`,
  };

  const variantClass = `mr-profile-avatar-wrap--${variant}`;

  const avatarContent = (
    <div
      className={`mr-profile-avatar-wrap ${variantClass} ${className}`}
      style={{ ...sizeStyle, ...style }}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <div className="mr-profile-avatar-inner">
        <img
          src={resolvedSrc}
          alt={alt}
          className="rounded-circle h-100 w-100 object-cover"
          style={{
            width: "100%",
            height: "100%",
            aspectRatio: "1 / 1",
            objectFit: "cover",
            borderRadius: "50%",
            display: "block",
          }}
          onError={(e) => {
            if (e.target.src !== Avatar && !e.target.src.includes("placeholder_Awatar")) {
              e.target.src = Avatar;
            }
          }}
          loading="lazy"
        />
      </div>
      {isOnline && <span className="mr-avatar-status-dot" title="Online" />}
    </div>
  );

  if (link) {
    return (
      <Link to={link} className="mr-profile-avatar-root" onClick={onClick}>
        {avatarContent}
      </Link>
    );
  }

  return avatarContent;
}

export default memo(ProfileAvatar);
